/* eslint-disable react/require-default-props */
import React, {
  createContext,
  ReactElement,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { ILogger, Log, SignoutResponse, User, UserManager } from 'oidc-client-ts';
import { ClientConfig, createUserManager } from '../utils/oidc-client';
import { getUserData, parseJwtToken, UserInfo } from '../utils/token';

interface AuthenticationRoutes {
  signinRedirect: string;
  signinPopup: string;
  signoutRedirect: string;
  signoutPopup: string;
  login: string;
}

export interface IAuthProps {
  authenticated?: boolean;
  inProgress?: boolean;
  routes: AuthenticationRoutes;

  signinSilent: () => Promise<User | null>;
  signinRedirect: (state?: object) => Promise<void>;
  signinRedirectCallback: () => Promise<User>;
  signinPopupCallback: () => Promise<User>;
  signoutRedirect: () => Promise<void>;
  signoutRedirectCallback: () => Promise<SignoutResponse>;
  signoutPopupCallback: () => Promise<void>;
  getUser: () => User | undefined;
  getUserInfo: () => UserInfo;
  updateUser: (user: User | null) => void;
  setLogger: (logger: ILogger) => void;
  refresh(): Promise<User | undefined>;
}

export const AuthenticationContext = createContext<IAuthProps>({} as IAuthProps);

export const useAuthentication = (): IAuthProps => {
  const context = useContext(AuthenticationContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthentificationProvider');
  }
  return context;
};

export interface AuthProviderProps {
  children?: ReactNode;
  config: ClientConfig;

  onUserLoaded?: (user: User) => void;
  onUsersignedIn?: () => void;
  onUserUnloaded?: () => void;
  onAccessTokenExpiring?: () => void;
  onAccessTokenExpired?: () => void;
  onUserSignedOut?: () => void;
}

async function handleSigninRedirect(userManager: UserManager, state?: object): Promise<void> {
  if (window.self !== window.top) {
    await userManager.signinPopup({
      state: state || window.location,
    });
  } else {
    await userManager.signinRedirect({
      state: state || window.location,
    });
  }
}

export const AuthenticationProvider = ({
  children,
  config,
  onUserLoaded,
  onUsersignedIn,
  onUserUnloaded,
  onAccessTokenExpiring,
  onAccessTokenExpired,
  onUserSignedOut,
}: AuthProviderProps): ReactElement => {
  const {
    loginRoute = '',
    signInRoute,
    signOutRoute,
    signInPopupRoute,
    signOutPopupRoute,
  } = { ...config };
  const bypass = config.bypassAuthentication;
  const userManager = useMemo(() => createUserManager(config), [config]);
  const [logger, setLogger] = useState<ILogger | undefined>();

  useEffect(() => {
    if (logger) {
      Log.setLogger(logger);
      Log.setLevel(Log.DEBUG);
    }
  }, [logger]);

  const userRef = useRef<User | undefined>(undefined);
  const [boundUserManager, setBoundUserManager] = useState<UserManager | null>(null);
  const [authenticated, setAuthenticated] = useState(bypass);
  const [inProgress, setInProgress] = useState(true);
  const [tryInitialLogin, setTryInitialLogin] = useState(true);
  const cancelInitialLogin = useRef(false);
  const silentSigninRef = useRef<Promise<User | null> | null>(null);

  const getUser = useCallback((): User | undefined => userRef.current, []);
  const getUserInfo = useCallback((): UserInfo => {
    const token = getUser()?.access_token;
    return getUserData(token ? parseJwtToken(token) : null);
  }, [getUser]);
  const updateUser = useCallback(
    (user: User | null): void => {
      userRef.current = user || undefined;
      setAuthenticated(bypass || !!user);
      setInProgress(false);
    },
    [bypass],
  );
  const signinSilent = useCallback(async (): Promise<User | null> => {
    let request = silentSigninRef.current;
    if (!request) {
      request = userManager.signinSilent();
      silentSigninRef.current = request;
    }

    try {
      const user = await request;
      updateUser(user);
      return user;
    } catch (error) {
      await userManager.removeUser();
      updateUser(null);
      throw error;
    } finally {
      if (silentSigninRef.current === request) {
        silentSigninRef.current = null;
      }
    }
  }, [updateUser, userManager]);

  useEffect(() => {
    if (bypass || !userManager || boundUserManager === userManager) {
      return undefined;
    }
    setBoundUserManager(userManager);

    const handleOnUsersignedIn = (): void => {
      (async () => {
        logger?.info('USER SIGNED IN');
        const user = await userManager.getUser();
        if (user) {
          updateUser(user);
        }

        if (onUsersignedIn) {
          onUsersignedIn();
        }
      })();
    };
    const handleOnUserLoaded = (user: User): void => {
      logger?.info('USER LOADED');
      updateUser(user);

      if (onUserLoaded) {
        onUserLoaded(user);
      }
    };
    const handleOnUserUnloaded = (): void => {
      logger?.info('USER UNLOADED');

      if (onUserUnloaded) {
        onUserUnloaded();
      }
    };
    const handleOnAccessTokenExpiring = (): void => {
      logger?.info('USER TOKEN EXPIRING');

      if (onAccessTokenExpiring) {
        onAccessTokenExpiring();
      }
    };
    const handleOnAccessTokenExpired = async (): Promise<void> => {
      logger?.info('USER TOKEN EXPIRED');

      if (config.allowResignin) {
        try {
          if (!(await signinSilent())) {
            handleSigninRedirect(userManager);
          }
        } catch {
          handleSigninRedirect(userManager);
        }
      }

      if (onAccessTokenExpired) {
        onAccessTokenExpired();
      }
    };
    const handleOnUserSignedOut = (): void => {
      logger?.info('USER SIGNED OUT');
      updateUser(null);

      if (onUserSignedOut) {
        onUserSignedOut();
      }
    };

    userManager.events.addUserLoaded(handleOnUserLoaded);
    userManager.events.addUserSignedIn(handleOnUsersignedIn);
    userManager.events.addUserUnloaded(handleOnUserUnloaded);
    userManager.events.addAccessTokenExpiring(handleOnAccessTokenExpiring);
    userManager.events.addAccessTokenExpired(handleOnAccessTokenExpired);
    userManager.events.addUserSignedOut(handleOnUserSignedOut);

    // try to read logged in user
    if (tryInitialLogin) {
      setTryInitialLogin(false);
      (async () => {
        try {
          const user = await userManager.getUser();
          if (cancelInitialLogin.current) {
            return;
          }

          if (user?.expired && config.allowResignin) {
            try {
              await signinSilent();
            } catch {
              handleSigninRedirect(userManager);
            }
          } else {
            updateUser(user);
          }
        } finally {
          if (!cancelInitialLogin.current) {
            setInProgress(false);
          }
        }
      })();
    }

    return () => {
      userManager.events.removeUserLoaded(handleOnUserLoaded);
      userManager.events.removeUserSignedOut(handleOnUserSignedOut);
      userManager.events.removeUserSignedIn(handleOnUsersignedIn);
      userManager.events.removeUserUnloaded(handleOnUserUnloaded);
      userManager.events.removeAccessTokenExpiring(handleOnAccessTokenExpiring);
      userManager.events.removeAccessTokenExpired(handleOnAccessTokenExpired);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userManager]);

  const value: IAuthProps = useMemo(
    () => ({
      routes: {
        signinRedirect: signInRoute,
        signoutRedirect: signOutRoute,
        signinPopup: signInPopupRoute,
        signoutPopup: signOutPopupRoute,
        login: loginRoute,
      },
      authenticated,
      inProgress: !bypass && inProgress,

      signinRedirect: (state?: object) => {
        logger?.info('SIGN IN REDIRECT');
        setInProgress(true);
        return handleSigninRedirect(userManager, state);
      },
      signinRedirectCallback: async () => {
        cancelInitialLogin.current = true;
        const user = await userManager.signinRedirectCallback();
        updateUser(user);
        return user;
      },
      signinPopupCallback: async () => {
        cancelInitialLogin.current = true;
        await userManager.signinPopupCallback();
        const user = await userManager.getUser();
        updateUser(user);
        return user as User;
      },
      getUser,
      getUserInfo,
      updateUser,
      signoutRedirect: () => {
        logger?.info('SIGN OUT REDIRECT');
        return userManager.signoutRedirect();
      },
      signoutRedirectCallback: async () => {
        const signout = await userManager.signoutRedirectCallback();
        updateUser(null);
        return signout;
      },
      signoutPopupCallback: async () => {
        const signout = await userManager.signoutPopupCallback();
        updateUser(null);
        return signout;
      },
      setLogger,
      signinSilent,
      async refresh() {
        const user = await userManager.getUser();
        updateUser(user);
        return user || undefined;
      },
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      authenticated,
      inProgress,
      signInRoute,
      signOutRoute,
      loginRoute,
      getUser,
      getUserInfo,
      updateUser,
      signinSilent,
    ],
  );

  return <AuthenticationContext.Provider value={value}>{children}</AuthenticationContext.Provider>;
};
